import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, X, Loader2, User, Phone, CreditCard, MapPin } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiFetch } from "@/lib/api";

// ✅ debounce
function useDebounce<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

type CustomerLite = {
  id: number | string;
  fname?: string;
  mname?: string;
  sname?: string;
  phone_number?: string;
  email?: string;
  national_id?: string;
  branch_name?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function GlobalCustomerSearch({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [q, setQ] = useState("");
  const dq = useDebounce(q, 350);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CustomerLite[]>([]);
  const [err, setErr] = useState<string>("");

  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const results = useMemo(() => rows ?? [], [rows]);

  const fullName = (c: CustomerLite) =>
    [c.fname, c.mname, c.sname].filter(Boolean).join(" ").trim();

  // ✅ Shortcut: Ctrl/Cmd+K open, Esc close, arrows & enter navigate/select
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const isMac = navigator.platform.toUpperCase().includes("MAC");

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping =
        tag === "input" || tag === "textarea" || (target as any)?.isContentEditable;

      const isShortcut =
        (isMac && e.metaKey && key === "k") || (!isMac && e.ctrlKey && key === "k");

      if (isShortcut) {
        e.preventDefault();
        onOpenChange(true);
        return;
      }

      if (key === "escape" && open) {
        e.preventDefault();
        onOpenChange(false);
        return;
      }

      if (!open) return;

      // allow keys while focusing our search input, block others if typing elsewhere
      if (
        isTyping &&
        !(
          tag === "input" &&
          (target as HTMLInputElement).id === "global-customer-search-input"
        )
      ) {
        return;
      }

      if (key === "arrowdown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      }

      if (key === "arrowup") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }

      if (key === "enter") {
        if (results.length === 0) return;
        e.preventDefault();
        const c = results[activeIndex];
        if (!c) return;

        const selectedId = String(c.id);
        sessionStorage.setItem("selectedCustomer", selectedId);
        onOpenChange(false);

        navigate("/customer-profile", {
          replace: location.pathname === "/customer-profile",
          state: { selectedCustomer: selectedId, nonce: Date.now() },
        });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange, results, activeIndex, navigate, location.pathname]);

  // Focus input when opening
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      inputRef.current?.focus?.();
      inputRef.current?.select?.();
    }, 60);
    return () => clearTimeout(t);
  }, [open]);

  // Clear when closing
  useEffect(() => {
    if (open) return;
    setQ("");
    setRows([]);
    setErr("");
    setLoading(false);
    setActiveIndex(0);
  }, [open]);

  // Search
  useEffect(() => {
    let alive = true;

    async function run() {
      const query = dq.trim();
      setErr("");

      if (!open) return;

      if (query.length < 2) {
        setRows([]);
        setActiveIndex(0);
        return;
      }

      try {
        setLoading(true);

        const res = await apiFetch<any>(
          `/customer-profile/search?q=${encodeURIComponent(query)}`
        );

        if (!alive) return;

        const list =
          (Array.isArray(res?.data) ? res.data : null) ||
          (Array.isArray(res?.data?.data) ? res.data.data : null) ||
          (Array.isArray(res?.data?.customers) ? res.data.customers : null) ||
          [];

        setRows(list);
        setActiveIndex(0);
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message || "Failed to search");
        setRows([]);
        setActiveIndex(0);
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [dq, open]);

  // ✅ Ensure active item scrolls into view
  useEffect(() => {
    if (!open) return;
    const el = itemRefs.current.get(activeIndex);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  const showHint = useMemo(() => {
    const trimmed = q.trim();
    if (trimmed.length === 0) return "Search by name, phone, national ID";
    if (trimmed.length < 2) return "Type at least 2 characters";
    return `${results.length} result(s)`;
  }, [q, results.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={[
          "[&>button]:hidden",
          // ✅ Mobile: full screen sheet
          "w-[100vw] h-[100dvh] max-w-none rounded-none p-0",
          // ✅ Tablet: centered card, comfortable width/height
          "sm:rounded-2xl sm:h-[88dvh] sm:w-[92vw] sm:max-w-[760px]",
          // ✅ Desktop: compact dialog on the side (like cmdk)
          "lg:h-[78vh] lg:w-[52vw] lg:max-w-[840px]",
          isRTL ? "lg:ml-6 lg:mr-auto" : "lg:mr-6 lg:ml-auto",
        ].join(" ")}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <DialogHeader className="px-3 sm:px-4 py-3 border-b border-border flex-row items-center justify-between gap-3">
            <DialogTitle className="text-base font-semibold">Search Customer</DialogTitle>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="shrink-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>

          {/* Search input */}
          <div className="px-3 sm:px-4 py-3 border-b border-border">
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                id="global-customer-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, phone, national ID..."
                className="ps-10 h-11 sm:h-10"
              />
            </div>

            <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-xs text-muted-foreground">
              <span>{showHint}</span>

              <div className="flex items-center gap-3">
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Searching...
                  </span>
                ) : null}

                {/* ✅ Hide long hint on mobile; show on tablet+ */}
                {results.length > 0 ? (
                  <span className="hidden md:inline">↑↓ navigate • Enter open • Esc close</span>
                ) : null}
              </div>
            </div>

            {err ? <div className="mt-2 text-sm text-destructive">{err}</div> : null}
          </div>

          {/* Results */}
          <div className="flex-1 overflow-auto">
            {q.trim().length < 2 ? (
              <div className="p-5 sm:p-6 text-sm text-muted-foreground">
                Start typing to search customers.
              </div>
            ) : !loading && results.length === 0 ? (
              <div className="p-5 sm:p-6 text-sm text-muted-foreground">
                No customers found.
              </div>
            ) : (
              <div className="p-2 sm:p-3 space-y-2">
                {results.map((c, i) => {
                  const name = fullName(c);
                  const active = i === activeIndex;

                  return (
                    <div
                      key={`${c.id}-${i}`}
                      ref={(el) => itemRefs.current.set(i, el)}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => {
                        const selectedId = String(c.id);
                        sessionStorage.setItem("selectedCustomer", selectedId);

                        onOpenChange(false);

                        navigate("/customer-profile", {
                          replace: location.pathname === "/customer-profile",
                          state: { selectedCustomer: selectedId, nonce: Date.now() },
                        });
                      }}
                      className={[
                        "cursor-pointer rounded-xl border transition",
                        "p-3 sm:p-4",
                        active
                          ? "border-primary/40 bg-muted/40 ring-1 ring-primary/20"
                          : "border-border bg-card hover:bg-muted/30",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="font-semibold truncate">
                              {name || `Customer #${c.id}`}
                            </div>
                          </div>

                          {/* ✅ Responsive details:
                              - Mobile: single column
                              - Tablet/Desktop: 2 columns */}
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-2 min-w-0">
                              <Phone className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{c.phone_number || "-"}</span>
                            </div>

                            {c.national_id ? (
                              <div className="flex items-center gap-2 min-w-0">
                                <CreditCard className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">ID: {c.national_id}</span>
                              </div>
                            ) : (
                              <div className="hidden sm:block" />
                            )}

                            {c.branch_name ? (
                              <div className="flex items-center gap-2 min-w-0 sm:col-span-2 text-[11px] opacity-80">
                                <MapPin className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">Branch: {c.branch_name}</span>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* ✅ Desktop-only helper label */}
                        <span className="hidden md:inline text-xs text-muted-foreground whitespace-nowrap">
                          Enter ↵
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ✅ Mobile footer actions (thumb friendly) */}
          <div className="border-t border-border p-3 sm:hidden">
            <Button variant="secondary" className="w-full" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}